/**
 * Multiplayer Mafia Service
 *
 * Extends GenericMultiplayerService for Mafia-specific multiplayer.
 * Handles room creation/joining, night actions, day voting, chat,
 * and reconnection with exponential backoff.
 *
 * Requirements: 1.1–1.5, 4.1, 4.3, 5.1, 6.1, 8.2, 9.1, 15.1, 16.1, 26.2
 */

import { WebSocketService } from '../../services/WebSocketService';
import {
  GenericMultiplayerService,
  GameResult,
} from '../../services/multiplayer/GenericMultiplayerService';
import {
  MafiaGameState,
  MafiaAction,
  MafiaGameConfig,
  MafiaRole,
  Team,
  GamePhase,
  PhaseData,
  NightResolutionResult,
  VotingResolutionResult,
  ChatMessage,
  InvestigationResult,
  MafiaGameResult,
  LobbyPlayer,
  ChatChannel,
} from '../types';

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_ATTEMPTS = 3;
const GAME_STATE_RETENTION_MS = 5 * 60 * 1000; // 5 minutes

export class MultiplayerMafiaService extends GenericMultiplayerService<
  MafiaGameState,
  MafiaAction
> {
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disconnectedAt: number | null = null;
  private extendedMode = false;

  // Mafia-specific callbacks
  private onRoleAssignedCallback?: (role: MafiaRole, team: Team, mafiaMembers?: string[]) => void;
  private onPhaseChangedCallback?: (phase: GamePhase, phaseData: PhaseData) => void;
  private onNightResultCallback?: (result: NightResolutionResult) => void;
  private onVoteUpdateCallback?: (votes: Map<string, string | null>) => void;
  private onVoteResultCallback?: (result: VotingResolutionResult) => void;
  private onChatMessageCallback?: (message: ChatMessage) => void;
  private onMafiaVoteUpdateCallback?: (votes: Map<string, string>) => void;
  private onInvestigationResultCallback?: (result: InvestigationResult) => void;
  private onPlayerEliminatedCallback?: (playerId: string, role?: MafiaRole) => void;
  private onGameOverMafiaCallback?: (result: MafiaGameResult) => void;
  private onLobbyUpdateCallback?: (players: LobbyPlayer[]) => void;
  private onConfigUpdateCallback?: (config: MafiaGameConfig) => void;
  private onTimerSyncCallback?: (phase: GamePhase, remaining: number) => void;
  private onReconnectionFailedCallback?: () => void;

  constructor(wsService?: WebSocketService) {
    super(wsService ?? WebSocketService.getInstance(), 'mafia');
  }

  // -----------------------------------------------------------------------
  // Abstract method implementations
  // -----------------------------------------------------------------------

  protected setupMessageHandlers(): void {
    this.wsService.setCustomMessageHandler((message: any) => {
      try {
        const type: string = message.type ?? '';

        switch (type) {
          case 'mafia:roomCreated':
            this.roomCode = message.roomCode ?? null;
            this.playerRole = 'host';
            if (this.onGameStartedCallback) this.onGameStartedCallback();
            break;

          case 'mafia:roomJoined':
            this.roomCode = message.roomCode ?? null;
            this.playerRole = 'player';
            if (this.onGameStartedCallback) this.onGameStartedCallback();
            break;

          case 'mafia:opponentJoined':
            if (this.onPlayerConnectedCallback) {
              this.onPlayerConnectedCallback(message.playerId ?? 'unknown');
            }
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'mafia:playerJoined':
            if (this.onPlayerConnectedCallback) {
              this.onPlayerConnectedCallback(message.playerId ?? 'unknown');
            }
            break;

          case 'mafia:playerLeft':
            if (this.onPlayerDisconnectedCallback) {
              this.onPlayerDisconnectedCallback(message.playerId ?? 'unknown');
            }
            break;

          case 'mafia:lobbyUpdate':
            if (this.onLobbyUpdateCallback && message.players) {
              this.onLobbyUpdateCallback(message.players);
            }
            break;

          case 'mafia:configUpdated':
            if (this.onConfigUpdateCallback && message.config) {
              this.onConfigUpdateCallback(message.config);
              if (message.config.extendedMode !== undefined) {
                this.extendedMode = message.config.extendedMode;
              }
            }
            break;

          case 'mafia:gameStarted':
            if (message.gameState) this.gameState = message.gameState;
            if (this.onGameStateReceivedCallback) {
              this.onGameStateReceivedCallback(message.gameState);
            }
            break;

          case 'mafia:roleAssigned':
            if (this.onRoleAssignedCallback) {
              this.onRoleAssignedCallback(
                message.role,
                message.team,
                message.mafiaMembers
              );
            }
            break;

          case 'mafia:phaseChanged':
            if (this.onPhaseChangedCallback) {
              this.onPhaseChangedCallback(message.phase, message.phaseData);
            }
            break;

          case 'mafia:timerSync':
            if (this.onTimerSyncCallback) {
              this.onTimerSyncCallback(message.phase, message.remaining);
            }
            break;

          case 'mafia:mafiaVoteUpdate':
            if (this.onMafiaVoteUpdateCallback && message.votes) {
              this.onMafiaVoteUpdateCallback(new Map(Object.entries(message.votes)));
            }
            break;

          case 'mafia:nightResult':
            if (this.onNightResultCallback) {
              this.onNightResultCallback(message.result);
            }
            break;

          case 'mafia:investigationResult':
            if (this.onInvestigationResultCallback) {
              this.onInvestigationResultCallback(message.result);
            }
            break;

          case 'mafia:chatMessage':
            if (this.onChatMessageCallback) {
              this.onChatMessageCallback(message.message);
            }
            break;

          case 'mafia:voteUpdate':
            if (this.onVoteUpdateCallback && message.votes) {
              const voteMap = new Map<string, string | null>();
              for (const [k, v] of Object.entries(message.votes)) {
                voteMap.set(k, v as string | null);
              }
              this.onVoteUpdateCallback(voteMap);
            }
            break;

          case 'mafia:voteResult':
            if (this.onVoteResultCallback) {
              this.onVoteResultCallback(message.result);
            }
            break;

          case 'mafia:playerEliminated':
            if (this.onPlayerEliminatedCallback) {
              this.onPlayerEliminatedCallback(message.playerId, message.role);
            }
            break;

          case 'mafia:gameOver': {
            if (this.onGameOverMafiaCallback) {
              this.onGameOverMafiaCallback(message.result);
            }
            const gameOverResult: GameResult = {
              result: 'win',
              winner: message.result?.winner,
              reason: message.result?.winReason,
            };
            if (this.onGameOverCallback) this.onGameOverCallback(gameOverResult);
            break;
          }

          case 'mafia:playerDisconnected':
            if (this.onPlayerDisconnectedCallback) {
              this.onPlayerDisconnectedCallback(message.playerId ?? 'unknown');
            }
            break;

          case 'mafia:playerReconnected':
            if (this.onPlayerConnectedCallback) {
              this.onPlayerConnectedCallback(message.playerId ?? 'unknown');
            }
            break;

          case 'mafia:gameState':
            if (message.gameState) this.gameState = message.gameState;
            if (this.onGameStateReceivedCallback) {
              this.onGameStateReceivedCallback(message.gameState);
            }
            this.resetReconnectionState();
            if (this.onConnectionStatusCallback) {
              this.onConnectionStatusCallback('connected');
            }
            break;

          case 'mafia:error':
            this.handleError(message.message ?? message.error ?? 'Unknown mafia error');
            break;

          default:
            break;
        }
      } catch (err) {
        this.handleError('Error handling mafia message', err);
      }
    });
  }

  protected sendCreateRoomMessage(): void {
    this.wsService.send({
      action: 'mafia:createRoom',
      playerId: this.playerId,
    });
  }

  protected sendJoinRoomMessage(roomCode: string): void {
    this.wsService.send({
      action: 'mafia:joinRoom',
      roomCode,
      playerId: this.playerId,
    });
  }

  protected sendLeaveRoomMessage(): void {
    if (this.roomCode) {
      this.wsService.send({
        action: 'mafia:leaveRoom',
        roomCode: this.roomCode,
        playerId: this.playerId,
      });
    }
  }

  protected getMaxPlayers(): number {
    return this.extendedMode ? 30 : 15;
  }

  // -----------------------------------------------------------------------
  // Move sending
  // -----------------------------------------------------------------------

  async sendMove(move: MafiaAction): Promise<boolean> {
    try {
      if (!this.roomCode) throw new Error('Not in a multiplayer game');

      this.wsService.send({
        action: `mafia:${move.type}`,
        roomCode: this.roomCode,
        playerId: this.playerId,
        targetId: move.targetId,
        message: move.message,
        channel: move.channel,
      });

      return true;
    } catch (error) {
      this.handleError('Failed to send mafia action', error);
      return false;
    }
  }

  isMyTurn(_currentTurn: string): boolean {
    // In Mafia, turns are phase-based, not player-based
    return true;
  }

  async resign(): Promise<void> {
    if (!this.roomCode) throw new Error('Not in a multiplayer game');
    this.wsService.send({
      action: 'mafia:leaveRoom',
      roomCode: this.roomCode,
      playerId: this.playerId,
    });
  }

  // -----------------------------------------------------------------------
  // Mafia-specific methods
  // -----------------------------------------------------------------------

  async sendStartGame(config: MafiaGameConfig): Promise<boolean> {
    try {
      if (!this.roomCode) throw new Error('Not in a room');
      this.wsService.send({
        action: 'mafia:startGame',
        roomCode: this.roomCode,
        playerId: this.playerId,
        config,
      });
      return true;
    } catch (error) {
      this.handleError('Failed to start game', error);
      return false;
    }
  }

  async sendMafiaVote(targetId: string): Promise<boolean> {
    return this.sendMove({
      type: 'mafiaVote',
      playerId: this.playerId || '',
      targetId,
      timestamp: Date.now(),
    });
  }

  async sendDoctorProtection(targetId: string | null): Promise<boolean> {
    return this.sendMove({
      type: 'doctorProtect',
      playerId: this.playerId || '',
      targetId: targetId || undefined,
      timestamp: Date.now(),
    });
  }

  async sendDetectiveInvestigation(targetId: string): Promise<boolean> {
    return this.sendMove({
      type: 'detectiveInvestigate',
      playerId: this.playerId || '',
      targetId,
      timestamp: Date.now(),
    });
  }

  async sendDayVote(targetId: string | null): Promise<boolean> {
    return this.sendMove({
      type: 'dayVote',
      playerId: this.playerId || '',
      targetId: targetId || undefined,
      timestamp: Date.now(),
    });
  }

  async sendChatMessage(message: string, channel: ChatChannel): Promise<boolean> {
    return this.sendMove({
      type: 'chatMessage',
      playerId: this.playerId || '',
      message,
      channel,
      timestamp: Date.now(),
    });
  }

  async sendGameConfig(config: MafiaGameConfig): Promise<boolean> {
    try {
      if (!this.roomCode) throw new Error('Not in a room');
      this.extendedMode = config.extendedMode;
      this.wsService.send({
        action: 'mafia:updateConfig',
        roomCode: this.roomCode,
        playerId: this.playerId,
        config,
      });
      return true;
    } catch (error) {
      this.handleError('Failed to update config', error);
      return false;
    }
  }

  async sendReadyStatus(ready: boolean): Promise<boolean> {
    try {
      if (!this.roomCode) throw new Error('Not in a room');
      this.wsService.send({
        action: 'mafia:ready',
        roomCode: this.roomCode,
        playerId: this.playerId,
        ready,
      });
      return true;
    } catch (error) {
      this.handleError('Failed to send ready status', error);
      return false;
    }
  }

  // -----------------------------------------------------------------------
  // Callback registration
  // -----------------------------------------------------------------------

  onRoleAssigned(callback: (role: MafiaRole, team: Team, mafiaMembers?: string[]) => void): void {
    this.onRoleAssignedCallback = callback;
  }

  onPhaseChanged(callback: (phase: GamePhase, phaseData: PhaseData) => void): void {
    this.onPhaseChangedCallback = callback;
  }

  onNightResult(callback: (result: NightResolutionResult) => void): void {
    this.onNightResultCallback = callback;
  }

  onVoteUpdate(callback: (votes: Map<string, string | null>) => void): void {
    this.onVoteUpdateCallback = callback;
  }

  onVoteResult(callback: (result: VotingResolutionResult) => void): void {
    this.onVoteResultCallback = callback;
  }

  onChatMessage(callback: (message: ChatMessage) => void): void {
    this.onChatMessageCallback = callback;
  }

  onMafiaVoteUpdate(callback: (votes: Map<string, string>) => void): void {
    this.onMafiaVoteUpdateCallback = callback;
  }

  onInvestigationResult(callback: (result: InvestigationResult) => void): void {
    this.onInvestigationResultCallback = callback;
  }

  onPlayerEliminated(callback: (playerId: string, role?: MafiaRole) => void): void {
    this.onPlayerEliminatedCallback = callback;
  }

  onGameOverMafia(callback: (result: MafiaGameResult) => void): void {
    this.onGameOverMafiaCallback = callback;
  }

  onLobbyUpdate(callback: (players: LobbyPlayer[]) => void): void {
    this.onLobbyUpdateCallback = callback;
  }

  onConfigUpdate(callback: (config: MafiaGameConfig) => void): void {
    this.onConfigUpdateCallback = callback;
  }

  onTimerSync(callback: (phase: GamePhase, remaining: number) => void): void {
    this.onTimerSyncCallback = callback;
  }

  onReconnectionFailed(callback: () => void): void {
    this.onReconnectionFailedCallback = callback;
  }

  // -----------------------------------------------------------------------
  // Connection management & exponential backoff reconnection
  // -----------------------------------------------------------------------

  handleDisconnect(): void {
    this.disconnectedAt = Date.now();
    if (this.onConnectionStatusCallback) {
      this.onConnectionStatusCallback('disconnected');
    }
    this.attemptReconnection();
  }

  private attemptReconnection(): void {
    if (this.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      if (this.onReconnectionFailedCallback) {
        this.onReconnectionFailedCallback();
      }
      this.handleError(
        'Max reconnection attempts reached. Please reconnect manually or return to mode selection.'
      );
      return;
    }

    const delay = RECONNECT_BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    if (this.onConnectionStatusCallback) {
      this.onConnectionStatusCallback('reconnecting');
    }

    this.reconnectTimer = setTimeout(async () => {
      try {
        await this.wsService.connect();
        if (this.roomCode && this.playerId) {
          this.wsService.send({
            action: 'mafia:reconnect',
            roomCode: this.roomCode,
            playerId: this.playerId,
          });
        }
      } catch {
        this.attemptReconnection();
      }
    }, delay);
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  isGameStateExpired(): boolean {
    if (this.disconnectedAt === null) return false;
    return Date.now() - this.disconnectedAt > GAME_STATE_RETENTION_MS;
  }

  setExtendedMode(enabled: boolean): void {
    this.extendedMode = enabled;
  }

  // -----------------------------------------------------------------------
  // Cleanup
  // -----------------------------------------------------------------------

  protected cleanup(): void {
    this.resetReconnectionState();
    this.disconnectedAt = null;
    this.extendedMode = false;
    this.onRoleAssignedCallback = undefined;
    this.onPhaseChangedCallback = undefined;
    this.onNightResultCallback = undefined;
    this.onVoteUpdateCallback = undefined;
    this.onVoteResultCallback = undefined;
    this.onChatMessageCallback = undefined;
    this.onMafiaVoteUpdateCallback = undefined;
    this.onInvestigationResultCallback = undefined;
    this.onPlayerEliminatedCallback = undefined;
    this.onGameOverMafiaCallback = undefined;
    this.onLobbyUpdateCallback = undefined;
    this.onConfigUpdateCallback = undefined;
    this.onTimerSyncCallback = undefined;
    super.cleanup();
  }

  private resetReconnectionState(): void {
    this.reconnectAttempts = 0;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
